import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";

/**
 * 운영 지표 스냅샷 조회. admin 전용 read-only 계약이다.
 *
 * 수집기가 5분마다 PostgreSQL global/table/query 와 API pod runtime·지연 histogram 의
 * 스냅샷을 크기가 제한된 테이블에 저장한다. 이 계약은 그 스냅샷을 창 단위로 읽는다.
 *
 * **원시 요청 로그·MCP tool 이벤트·감사 로그는 여기 없다.** Cloud Logging 이 소유한다.
 * operation 단위 지연 **집계**는 여기 있다. 집계 행은 도메인 크기가 아니라 `시간 × top-K` 로
 * 늘어나서 크기가 제한되기 때문이다(backend `docs/trade-offs/observability-snapshot-storage-trade-off.md` §1).
 * 두 데이터를 섞어 읽지 않는다. 여기 있는 것은 분포이고, 개별 호출이 아니다.
 *
 * Owns:
 * - wire 타입과 다상태(`MetricUnit` 의 "표본 없음")의 의미, 그 판정 헬퍼.
 * - 창 어휘와 요청 함수.
 *
 * Does not own:
 * - 표시 포맷·차트 계약 변환. `page/observability/observabilityModel.ts` 가 소유한다.
 * - URL ↔ 창/탭 코덱. `page/observability/observabilityWindow.ts` 가 소유한다.
 *
 * ## 이 계약의 다상태 두 가지 (`docs/rules.md` §16)
 *
 * 1. **`MetricUnit` 에서 "값 0" 과 "표본 없음" 은 다르다.** `samples === 0` 이면 창 안에 값이
 *    있는 tick 이 하나도 없었다. 이때 `mean`·`min`·`peak.value` 는 모두 `null` 이다. `?? 0` 으로
 *    바꾸면 수집이 멈춘 구간을 "0 이었다" 로 읽는다. `stdev` 는 `samples < 2` 이면 `null` 이다.
 *    이것은 산포가 정의되지 않는다는 뜻이고, 0 이 아니다.
 * 2. **창 요약과 시계열은 다른 구간을 뜻한다.** 요약(`MetricUnit`)은 창 전체를 본다.
 *    `points` 는 서버 상한 500점으로 자른 **최근 끝부분**이다. 5분 주기면 약 41시간이다.
 *    그래서 `30d` 창의 `points` 평균은 창 평균과 수십 배 다를 수 있다. 요약 자리에 `points` 로
 *    계산한 값을 쓰지 않는다.
 */

/** 조회 구간. 서버가 고정한 어휘다. 이 다섯 값 밖의 값은 422 다. */
export type MetricWindow = "1h" | "6h" | "24h" | "7d" | "30d";

/** 어휘 전체. URL 정규화와 컨트롤 선택지가 같은 배열을 쓴다. */
export const METRIC_WINDOWS: readonly MetricWindow[] = ["1h", "6h", "24h", "7d", "30d"] as const;

/** 서버 기본값. 창을 지정하지 않은 요청과 같은 구간이다. */
export const DEFAULT_METRIC_WINDOW: MetricWindow = "24h";

/** 수집 주기(초). 스냅샷 tick 사이 간격의 기준값이다. 서버 설정 기본값과 같다. */
export const COLLECTION_INTERVAL_SECONDS = 300;

/** 시계열 응답의 서버 상한(점). `points` 가 이 수와 같으면 더 오래된 구간이 잘린 것이다. */
export const SERIES_LIMIT = 500;

/** hotspot 응답의 서버 상한(행). tables·queries 가 같은 값을 쓴다. */
export const HOTSPOT_LIMIT = 20;

/**
 * 지연 응답의 서버 상한(operation). 상한은 **surface 별**이다.
 *
 * 전역 상한이 아닌 것이 중요하다. 정렬이 호출 수 내림차순이라, 전역으로 자르면 "드물지만
 * 느린" operation 이 먼저 잘린다. 이 축을 보는 이유가 바로 그런 호출이다(상류 config 조회는
 * 5시간에 15건이다). surface 별로 자르면 호출이 많은 surface 가 적은 surface 를 목록에서
 * 밀어내지 못한다.
 */
export const LATENCY_OPERATION_LIMIT_PER_SURFACE = 250;

/**
 * 최댓값 한 점. 값과 그 시각을 담는다.
 *
 * 값만으로는 다음 행동을 정할 수 없다. 그래서 시각도 함께 온다. 메모리가 7.8GB 까지 올랐다는
 * 사실보다 **언제** 올랐는지가 중요하다. 그 시각으로 같은 구간의 다른 지표를 확인한다.
 */
export type MetricPeak = {
  value: number | null;
  at: string | null;
};

/**
 * 지표 하나를 창 하나로 집계한 단위.
 *
 * 표본은 수집 주기마다 하나뿐이다. 그래서 이 단위는 **tick 사이에 일어난 일을 모른다.** 두 tick
 * 사이에 올랐다가 내려온 최댓값은 어느 표본에도 없다. 그래서 `peak` 과 `stdev` 는 실제 값의
 * 하한이다.
 */
export type MetricUnit = {
  /** 값이 있던 표본 수. 빈 tick(첫 사이클·통계 리셋)은 세지 않는다. */
  samples: number;
  mean: number | null;
  /** 표본이 하나면 산포가 정의되지 않아 비어 있다. 0 이 아니다. */
  stdev: number | null;
  min: number | null;
  peak: MetricPeak;
};

/** 수집 사이클의 마지막 결과. */
export type CollectionStatus = "ok" | "partial" | "failed";

/** 수집 자체의 상태. 지표를 믿어도 되는지 먼저 알려 준다. */
export type CollectionSummaryResp = {
  window: MetricWindow;
  cycles: number;
  failed_cycles: number;
  partial_cycles: number;
  last_captured_at: string | null;
  /** 서버가 어휘를 확장할 수 있게 열어 둔다. 새 상태는 raw 값으로 표시될 수 있다. */
  last_status: CollectionStatus | string | null;
  /** 사이클 사이의 최대 간격(초). 주기보다 훨씬 크면 그 구간의 지표가 **없다**. */
  max_gap_seconds: number | null;
};

/** 전역 DB 지표 한 시점. `*_delta` 는 직전 tick 대비 증분이다. */
export type DatabasePoint = {
  captured_at: string;
  active_sessions: number;
  idle_sessions: number;
  idle_in_transaction_sessions: number;
  waiting_sessions: number;
  oldest_transaction_seconds: number | null;
  commits_delta: number | null;
  rollbacks_delta: number | null;
  deadlocks_delta: number | null;
  block_hits_delta: number | null;
  block_reads_delta: number | null;
  temp_bytes_delta: number | null;
  /**
   * 읽기 증폭의 원재료. 읽은 양과 반환한 양이다.
   *
   * 비율이 아니라 두 값이 그대로 온다. tick 마다 나눈 값을 나중에 평균하면 요청이 적은 tick 과
   * 많은 tick 이 같은 가중치를 갖는다. 그래서 창 전체를 집계할 때는 합끼리 나눠야 한다.
   */
  tup_returned_delta: number | null;
  tup_fetched_delta: number | null;
  wal_bytes_delta: number | null;
  database_size_bytes: number | null;
};

/**
 * 전역 DB 지표. 창 요약과 최근 점 목록을 담는다.
 *
 * 요약을 먼저 본다. `points` 는 상한에서 잘리지만 요약은 창 전체를 본다.
 */
export type DatabaseMetricsResp = {
  window: MetricWindow;
  active_sessions: MetricUnit;
  waiting_sessions: MetricUnit;
  oldest_transaction_seconds: MetricUnit;
  /** 열려 있는 모든 커넥션. active + idle + idle in transaction 이다. 상한은 이 합에 적용된다. */
  connections: MetricUnit;
  /** 그 합의 한도. 설정값이라 창 안에서 바뀌지 않는다. 점유의 분모로만 쓴다. */
  max_connections: number | null;
  /** 증분 축의 단위는 "tick 하나당 양" 이다. `peak` 은 값이 가장 컸던 tick 을 가리킨다. */
  commits_delta: MetricUnit;
  /** 정렬·해시가 `work_mem` 을 넘어 디스크에 쓴 양. 읽기 경로의 문제가 이 값에 나타난다. */
  temp_bytes_delta: MetricUnit;
  wal_bytes_delta: MetricUnit;
  points: DatabasePoint[];
};

/**
 * 구간 안에서 한 테이블의 값. 읽기·인덱스·쓰기 전파 세 축이다.
 *
 * **값이 세 종류라 읽는 방법이 다르다.**
 * - 집계 축(`MetricUnit`): 게이지를 창 전체로 집계한 값이다. 행 수·크기·통계 지연이 해당한다.
 *   값이 계속 높았는지, 한 번만 올랐는지에 따라 조치가 다르다. 그래서 값 하나로 줄이지 않는다.
 * - 창 합(스칼라): tick 증분을 창 안에서 더한 값이다. 스캔·블록·행 쓰기가 해당한다.
 *   비율(`cache_hit_ratio`·`non_hot_update_ratio`)도 서버가 창 합끼리 나눠 보낸다. 분모가 0 이면
 *   `null` 이고, 0 이 아니다.
 * - 마지막 tick(스칼라): 인덱스 집계다. 미사용 인덱스는 통계 리셋 이후 `idx_scan = 0` 인 인덱스다.
 *   PK·unique 는 제외한다. `unused_index_names` 가 빈 배열이면 "미사용 없음" 이다.
 *   `unused_index_count` 가 `null` 이면 "아직 센 적 없음" 이다. 두 상태를 같게 취급하지 않는다.
 *
 * 서버는 크기 상위 20개 테이블을 블록 접근 합 내림차순으로 준다. 이 20개가 대상 전체다. 그래서
 * 화면의 열 정렬은 순서만 바꾼다.
 */
export type TableHotspotResp = {
  schema_name: string;
  table_name: string;
  live_tuples: MetricUnit;
  heap_bytes: MetricUnit;
  index_bytes: MetricUnit;
  rows_since_analyze: MetricUnit;
  block_accesses: number | null;
  block_reads: number | null;
  cache_hit_ratio: number | null;
  sequential_scans: number | null;
  sequential_rows_read: number | null;
  index_scans: number | null;
  index_rows_fetched: number | null;
  rows_written: number | null;
  rows_inserted: number | null;
  rows_updated: number | null;
  rows_deleted: number | null;
  non_hot_update_ratio: number | null;
  last_autoanalyze_at: string | null;
  index_count: number | null;
  unused_index_count: number | null;
  unused_index_bytes: number | null;
  unused_index_names: string[];
};

/**
 * 구간 안에서 한 fingerprint 가 쓴 시간. SQL 원문은 오지 않는다.
 *
 * **시간 필드가 두 종류라 섞어 읽으면 안 된다.**
 * - `calls`·`total_exec_ms`·`mean_exec_ms` 는 **이 구간**의 값이다. 스냅샷 증분을 더해 만든다.
 *   창을 바꾸면 이 값도 바뀐다.
 * - `min_exec_ms`·`max_exec_ms`·`stddev_exec_ms` 는 상류가 **통계 리셋 이후 누적**으로 계산한
 *   게이지를 구간 안에서 집계한 값이다. 리셋이 드물면 사실상 "지금까지 전체" 를 뜻한다. 창을
 *   좁혀도 값이 잘 줄지 않는다. 그래도 이 값을 두는 이유가 있다. 평균만으로는 "항상 느린
 *   쿼리"와 "가끔 크게 느려지는 쿼리"를 구분할 수 없다.
 *
 * `query_id` 는 **문자열**로 온다. int8 은 JSON number 로 보낼 수 없기 때문이다. 브라우저는 JSON
 * number 를 IEEE double 로 읽는다. 2^53 을 넘으면 하위 자릿수를 잃는다. `queryid` 는 해시라 거의
 * 항상 그 범위를 넘는다. 그래서 정수로 받으면 거의 모든 값이 파싱할 때 경고 없이 정밀도를 잃는다
 * (`7046933367344580123` → `7046933367344581000`). 정밀도를 잃은 값은 `WHERE queryid = ...` 에
 * 쓸 수 없다. 그것이 이 필드의 유일한 용도다. 그래서 **숫자로 바꾸지 않는다.**
 *
 * 이 값은 재시작·통계 리셋을 넘는 영구 식별자가 아니다.
 */
export type QueryHotspotResp = {
  query_id: string;
  calls: number | null;
  total_exec_ms: number | null;
  mean_exec_ms: number | null;
  min_exec_ms: number | null;
  max_exec_ms: number | null;
  stddev_exec_ms: number | null;
  shared_blocks_read: number | null;
};

/** API pod 한 개의 한 시점. */
export type RuntimePoint = {
  captured_at: string;
  pod_name: string;
  revision: string | null;
  memory_working_set_bytes: number | null;
  memory_limit_bytes: number | null;
  cpu_millicores: number | null;
  rss_bytes: number | null;
  event_loop_lag_ms: number | null;
  db_pool_checked_out: number | null;
  db_pool_overflow: number | null;
  upstream_pool_active: number | null;
};

/**
 * pod 한 개를 창 하나로 집계한 값. replica 하나에만 누수가 있을 때 합계에 가려지지 않도록
 * pod 별로 온다.
 *
 * **이 목록은 지금 실행 중인 replica 목록이 아니다.** 창 안에서 한 번이라도 관측된 pod 은 모두
 * 한 줄씩 온다. 그래서 롤아웃으로 사라진 pod 도 창을 벗어나기 전까지 남는다.
 */
export type RuntimePodMetricsResp = {
  pod_name: string;
  /** 창 안 마지막 관측 시각. 끝난 pod 이 언제까지 있었는지 이 값으로 안다. */
  last_seen_at: string | null;
  /**
   * 지금도 실행 중인가. 창 안 runtime 스냅샷의 최신 tick 에 이 pod 이 있었는지 나타낸다.
   *
   * 판정은 서버가 소유한다. 화면이 `points` 의 마지막 시각으로 다시 판정하면 기준이 둘이 된다.
   * 그러면 500점 상한에서 잘린 pod 은 실행 중인데도 끝난 것으로 보인다.
   */
  active: boolean;
  memory_working_set_bytes: MetricUnit;
  cpu_millicores: MetricUnit;
  event_loop_lag_ms: MetricUnit;
  db_pool_checked_out: MetricUnit;
  /** 상류(training) HTTP 커넥션 풀 점유. 풀 구조를 읽을 수 없는 런타임에서는 표본이 없다. */
  upstream_pool_active: MetricUnit;
  /**
   * 그 풀의 한도. 설정값이라 창 안에서 바뀌지 않는다.
   *
   * 점유의 **분모**로만 쓴다. 이 값이 없으면 화면은 "점유 2 가 많은 것인가" 를 판정할 수 없다.
   * 그러면 상류가 느릴 때 병목이 풀 대기인지 상류 자체인지 구분할 수 없다.
   */
  upstream_pool_max: number | null;
};

/**
 * 런타임 지표. pod 별 창 요약과 최근 점 목록을 담는다.
 *
 * `points` 의 500점 상한은 pod 별이 아니라 **전체 행 기준**이다. pod 이 셋이면 pod 당 실제
 * 해상도가 1/3 로 줄어든다. 점은 pod 별로 나뉘어 오지 않는다. 그래서 소비처가 나눠야 한다
 * (`groupRuntimePointsByPod`).
 */
export type RuntimeMetricsResp = {
  window: MetricWindow;
  pods: RuntimePodMetricsResp[];
  points: RuntimePoint[];
};

/** 지연 축의 호출 경계 어휘. 요청을 받는 쪽 넷과 요청을 보내는 쪽 하나다. */
export type LatencySurface = "api" | "admin_api" | "internal_api" | "mcp" | "upstream";

/**
 * 창 안에서 실제로 가장 오래 걸린 호출 하나와 그 로그 상관 키.
 *
 * `request_id` 가 이 화면의 핵심이다. 같은 값이 받는 쪽 행(`api ...`)과 보내는 쪽
 * 행(`upstream ...`)에 함께 있다. 두 행을 이 값으로 연결하면 "이 API 의 30초가 상류 hop
 * 하나였다" 를 Cloud Logging 없이 화면에서 확인할 수 있다. 그래서 이 값을 잘라 숨기지 않는다.
 */
export type LatencyObservedMaxResp = {
  duration_ms: number;
  observed_at: string;
  request_id: string | null;
  trace: string | null;
};

/**
 * surface·operation 하나의 창 요약.
 *
 * **`p50_ms`/`p95_ms` 는 고정 histogram bucket 의 상한이다.** 값이 든 칸의 위 경계다.
 * 그래서 "p95 가 정확히 2,500ms" 가 아니라 "2,500ms 칸 안" 이라는 뜻이다. bucket 보다 정밀한
 * 숫자로 표시하면 안 된다. `null` 은 그 percentile 이 60초 초과(열린) 구간에 있고 실측 최대도
 * 없어서 상한을 만들 수 없는 경우다. 0 이 아니다.
 *
 * `error_rate` 는 서버가 계산한다(`count` 가 0 이면 0). 오류로 세는 기준은 surface 마다 다르다:
 * - 받는 쪽: 5xx
 * - `mcp`: timeout·internal error
 * - `upstream`: transport 실패(타임아웃 포함)와 5xx
 * 4xx·도메인 거절은 어느 축에서도 오류가 아니다.
 */
export type LatencyOperationMetricsResp = {
  surface: LatencySurface;
  operation: string;
  count: number;
  error_count: number;
  error_rate: number;
  p50_ms: number | null;
  p95_ms: number | null;
  observed_max: LatencyObservedMaxResp | null;
};

/**
 * 지연 지표. surface·operation 별 창 요약이다.
 *
 * `operations` 는 **surface 별 호출 수 상위 250**(`LATENCY_OPERATION_LIMIT_PER_SURFACE`)이다.
 * 서버는 호출 수 내림차순으로 정렬한다. 화면은 "느린 것" 을 보려고 한다. 그래서 서버 순서를
 * 그대로 쓰지 않고, 소비처가 실측 최대 순으로 다시 정렬한다.
 *
 * `bucket_upper_bounds_ms` 는 histogram 해상도를 알려 주는 값이다. p50/p95 가 어느 칸의
 * 상한인지 읽으려면 이 목록이 필요하다.
 */
export type LatencyMetricsResp = {
  window: MetricWindow;
  bucket_upper_bounds_ms: number[];
  operations: LatencyOperationMetricsResp[];
};

function windowParams(window: MetricWindow) {
  return { params: { window } };
}

export async function getObservabilitySummary(
  window: MetricWindow,
): Promise<CollectionSummaryResp> {
  const { data } = await apiClient.get<CollectionSummaryResp>(
    apiRoutes.admin.observabilitySummary,
    windowParams(window),
  );
  return data;
}

export async function getObservabilityDatabase(window: MetricWindow): Promise<DatabaseMetricsResp> {
  const { data } = await apiClient.get<DatabaseMetricsResp>(
    apiRoutes.admin.observabilityDatabase,
    windowParams(window),
  );
  return data;
}

export async function getObservabilityTables(window: MetricWindow): Promise<TableHotspotResp[]> {
  const { data } = await apiClient.get<TableHotspotResp[]>(
    apiRoutes.admin.observabilityTables,
    windowParams(window),
  );
  return data;
}

export async function getObservabilityQueries(window: MetricWindow): Promise<QueryHotspotResp[]> {
  const { data } = await apiClient.get<QueryHotspotResp[]>(
    apiRoutes.admin.observabilityQueries,
    windowParams(window),
  );
  return data;
}

export async function getObservabilityRuntime(window: MetricWindow): Promise<RuntimeMetricsResp> {
  const { data } = await apiClient.get<RuntimeMetricsResp>(
    apiRoutes.admin.observabilityRuntime,
    windowParams(window),
  );
  return data;
}

export async function getObservabilityLatency(window: MetricWindow): Promise<LatencyMetricsResp> {
  const { data } = await apiClient.get<LatencyMetricsResp>(
    apiRoutes.admin.observabilityLatency,
    windowParams(window),
  );
  return data;
}

/**
 * 창 안에 이 지표의 표본이 하나라도 있었는가.
 *
 * 소비처가 `mean === null` 을 직접 보지 않게 하는 판정 함수다. `samples` 와 `mean` 중 어느 쪽을
 * 볼지 매번 다시 정하면, 한쪽만 검사하는 코드가 생긴다.
 */
export function hasMetricSamples(unit: MetricUnit): boolean {
  return unit.samples > 0;
}

/**
 * 지표의 평균. 지표가 없거나 표본이 없으면 `null` 이다.
 *
 * 응답 타입은 지표 키를 필수로 선언한다. 그러나 응답을 런타임에 검증하지 않는다. FE 가 BE 보다 먼저
 * 배포되면, 구 BE 응답에는 새 지표 키가 없어 `undefined` 가 온다. 소비처가 `unit.mean` 을 직접 읽으면
 * 렌더 중에 throw 한다. 앱에 ErrorBoundary 가 없어서 화면이 빈 화면이 된다. 그래서 값을 읽는
 * 소비처는 이 함수를 거친다.
 */
export function metricMean(unit: MetricUnit | undefined): number | null {
  return unit && hasMetricSamples(unit) ? unit.mean : null;
}

/**
 * 산포를 표시할 수 있는가. 표본이 둘 이상이라 `stdev` 가 정의되는지 판정한다.
 *
 * `stdev === null` 은 두 가지 뜻을 가진다:
 * - 표본이 없다(0).
 * - 표본이 하나뿐이라 산포가 정의되지 않는다(1).
 * 화면은 이 둘을 다른 문구로 표시해야 한다. 그래서 판정을 여기 둔다.
 */
export function hasMetricSpread(unit: MetricUnit): boolean {
  return unit.samples >= 2 && unit.stdev !== null;
}

/**
 * 이 창에 수집 사이클이 있었는가.
 *
 * **창 안의 판정이다.** 응답의 `cycles` 와 `last_captured_at` 은 창 범위로 잘려서 온다.
 * 그래서 false 가 "수집기가 실행되지 않는다" 를 뜻하지는 **않는다.** 좁은 창을 골랐고 그 사이에
 * 수집이 멈춰 있었을 수도 있다. 두 상태는 안내 문구가 달라야 한다. 구분하려면 보존 구간 전체를
 * 한 번 더 조회해야 한다(`RETENTION_METRIC_WINDOW`).
 */
export function hasCyclesInWindow(summary: CollectionSummaryResp): boolean {
  return summary.cycles > 0;
}

/**
 * 보존 구간 전체를 덮는 창.
 *
 * 수집기는 30일치만 남기고 그보다 오래된 스냅샷을 지운다. 그래서 이 창에도 사이클이 없으면
 * **남아 있는 기록이 하나도 없다.** 이것은 수집기가 실행되지 않고 있다는 뜻이다.
 */
export const RETENTION_METRIC_WINDOW: MetricWindow = "30d";

/**
 * 사이클이 주기보다 크게 벌어진 구간이 있었는가.
 *
 * 간격이 주기의 두 배를 넘어야 경고로 본다. 스냅샷 시각은 tick 처리 시간만큼 달라진다.
 * 그래서 주기를 조금 넘는 것은 정상이다. true 면 그 구간의 지표는 값이 0 이 아니라 **없는** 것이다.
 */
export function hasCollectionGap(summary: CollectionSummaryResp): boolean {
  return (
    summary.max_gap_seconds !== null && summary.max_gap_seconds > COLLECTION_INTERVAL_SECONDS * 2
  );
}

/**
 * 시계열이 서버 상한에서 잘렸는가. 창의 앞부분이 응답에 없는지 판정한다.
 *
 * 잘렸으면 화면의 차트 구간과 옆에 있는 창 요약의 구간이 다르다. 캡션이 그 사실을 표시해야 한다.
 */
export function isSeriesTruncated(points: readonly unknown[]): boolean {
  return points.length >= SERIES_LIMIT;
}

/**
 * 런타임 시계열을 pod 별로 나눈다. pod 순서는 처음 나온 순서를 유지한다.
 *
 * 서버는 모든 pod 의 점을 한 배열에 섞어 준다. 500점 상한도 pod 들이 나눠 쓴다. pod 별 라인을
 * 그리려면 소비처가 점을 나눠야 한다. pod 마다 점 수가 다르다는 사실도 화면에 나타나야 한다.
 */
export function groupRuntimePointsByPod(
  points: readonly RuntimePoint[],
): Map<string, RuntimePoint[]> {
  const byPod = new Map<string, RuntimePoint[]>();
  for (const point of points) {
    const bucket = byPod.get(point.pod_name);
    if (bucket === undefined) {
      byPod.set(point.pod_name, [point]);
    } else {
      bucket.push(point);
    }
  }
  return byPod;
}
